import sortBy from 'lodash/sortBy';

import {
  DragItem,
  DropZone,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import {
  DragItemSmall,
  DropItem as DropItemSmall,
} from './interactive-response-categorise-small.constants';
import { shuffleArray } from '../../../shared/interactive-drag-drop/interactive-drag-drop.utils';
import {
  InteractiveResponseCategoriesCategory,
  InteractiveResponseCategoriseItem,
} from '../interactive-response-categorise.constants';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import { InquisitiveActivitiesResponseVariant } from 'global/constants';

export function initialiseDragItems({
  items,
  shouldShuffle,
  variant,
}: {
  items: InteractiveResponseCategoriseItem[];
  shouldShuffle: boolean;
  variant: InquisitiveActivitiesResponseVariant;
}): DragItem[] {
  const sortedItems = sortBy(items, 'sort');
  const shuffled = shouldShuffle ? shuffleArray([...sortedItems]) : sortedItems;

  const dragItems = shuffled.map(item => ({
    id: item.itemId,
    value: item.categoryId || '',
    isDropped: false,
    question: {
      label: item.questionLabel || '1',
      text: item.text,
      richtext: item.richtext,
      audioUrl: item.audio?.original || null,
      baseColour: item.baseColour,
      variant: item.variant || variant,
      image: {
        itemId: item.itemId,
        small: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
        large: item?.imageUrls?.[1]?.urls?.[0]?.url || null,
        description: item.image?.description || '',

        // existing content in prod will have imageMode = null
        isFit: item.imageMode === null ? false : !item.cropData,
      },
    },
  }));
  return dragItems;
}

type Answers = {
  id: string;
  isCorrect: boolean;
  label: string;
  zones: DropZone[];
};

export function getAnswers(
  attemptedAnswers: InteractiveResponseAnswerFragment,
  items: InteractiveResponseCategoriseItem[],
  categories: InteractiveResponseCategoriesCategory[],
  showAnswer?: boolean
) {
  const sortedItems = sortBy(items, 'sort');
  const answers: Answers[] = categories.map(category => ({
    id: category.id,
    isCorrect: false,
    label: category.label,
    zones: [],
  }));

  for (let i = 0; i < attemptedAnswers.interactiveResponseItems.length; i++) {
    const attemptedItem = attemptedAnswers.interactiveResponseItems[i];
    const item = sortedItems.find(
      item => item.itemId === attemptedItem.imageId
    );
    const answerZone = answers.findIndex(
      answer => answer.id === attemptedItem.dropZoneId
    );
    const isCorrect =
      showAnswer === false ? undefined : attemptedItem.isCorrect || false;

    answers[answerZone].zones.push({
      dragItemId: item?.itemId || '',
      previewItemId: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
      isCorrect,
    });
  }

  return answers;
}

function initialiseDropItems({
  items,
  categories,
  showAnswer,
  isShowNumberOfItemsHint,
}: {
  items: InteractiveResponseCategoriseItem[];
  categories: InteractiveResponseCategoriesCategory[];
  showAnswer: boolean;
  isShowNumberOfItemsHint: boolean;
}) {
  const dropItems = categories.map(category => {
    const zones: DropZone[] = [];
    items.forEach(item => {
      // for free responses and when hints are hidden add equal dropzones on each side since
      // its mayhem and there are no real answers
      if (
        !showAnswer ||
        !isShowNumberOfItemsHint ||
        item.categoryId === category.id
      ) {
        zones.push({
          dragItemId: null,
          previewItemId: null,
          isCorrect: undefined,
        });
      }
    });
    return {
      id: category.id,
      label: category.label,
      zones,
    };
  });
  return dropItems;
}

type DropItem = {
  id: string;
  label: string;
  zones: DropZone[];
};

export function populateAnswers({
  attemptedAnswers,
  items,
  categories,
  variant,
  isShowNumberOfItemsHint,
  showAnswer,
}: {
  attemptedAnswers: InteractiveResponseAnswerFragment;
  items: InteractiveResponseCategoriseItem[];
  categories: InteractiveResponseCategoriesCategory[];
  variant: InquisitiveActivitiesResponseVariant;
  isShowNumberOfItemsHint: boolean;
  showAnswer: boolean;
}) {
  const dropItems = initialiseDropItems({
    items,
    categories,
    showAnswer,
    isShowNumberOfItemsHint,
  });

  for (let i = 0; i < attemptedAnswers.interactiveResponseItems.length; i++) {
    const attemptedItem = attemptedAnswers.interactiveResponseItems[i];
    const item = items.find(item => item.itemId === attemptedItem.imageId);
    const dropIndex = dropItems.findIndex(
      dropItem => dropItem.id === attemptedItem.dropZoneId
    );
    const isCorrect = !showAnswer
      ? undefined
      : attemptedItem.isCorrect || false;

    // find the next available zone and assign the item to it
    const nextAvailableZoneIndex = dropItems[dropIndex].zones.findIndex(
      zone => zone.dragItemId === null
    );

    if (nextAvailableZoneIndex !== -1) {
      dropItems[dropIndex].zones[nextAvailableZoneIndex] = {
        dragItemId: item?.itemId || '',
        previewItemId: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
        isCorrect,
      };
    }
  }

  const dragItems = initialiseDragItems({
    items,
    shouldShuffle: false,
    variant,
  });

  dragItems.map(dragItem => {
    dragItem.isDropped = true;
    return dragItem;
  });

  return {
    dragItems,
    dropItems,
  };
}

export function populateAnswersSmall({
  attemptedAnswers,
  items,
  categories,
  isFreeResponse,
  variant,
  showAnswer,
}: {
  attemptedAnswers: InteractiveResponseAnswerFragment;
  items: InteractiveResponseCategoriseItem[];
  categories: InteractiveResponseCategoriesCategory[];
  isFreeResponse?: boolean;
  variant: InquisitiveActivitiesResponseVariant;
  showAnswer?: boolean;
}) {
  const dropItems: DropItemSmall[] = [
    ...categories.map(category => ({
      id: category.id,
      label: category.label,
      dragItems: {},
    })),
  ];
  for (let i = 0; i < attemptedAnswers.interactiveResponseItems.length; i++) {
    const attemptedItem = attemptedAnswers.interactiveResponseItems[i];
    const item = items.find(item => item.itemId === attemptedItem.imageId);
    const dropIndex = dropItems.findIndex(
      dropItem => dropItem.id === attemptedItem.dropZoneId
    );
    const isCorrect =
      isFreeResponse || showAnswer === false
        ? undefined
        : attemptedItem.isCorrect || false;

    dropItems[dropIndex].dragItems[item?.itemId || ''] = {
      isCorrect,
      isAnswered: true,
    };
  }

  const dragItems: DragItemSmall[] = initialiseDragItems({
    items,
    shouldShuffle: false,
    variant,
  });

  dragItems.map(dragItem => {
    dragItem.isDropped = true;
    dragItem.isCorrect = dropItems.some(
      dropItem => dropItem.dragItems[dragItem.id]?.isCorrect
    );
    return dragItem;
  });

  return {
    dragItems,
    dropItems,
  };
}

export function initialise({
  items,
  categories,
  showAnswer,
  isShowNumberOfItemsHint,
  shouldShuffle = true,
  variant,
}: {
  items: InteractiveResponseCategoriseItem[];
  categories: InteractiveResponseCategoriesCategory[];
  showAnswer: boolean;
  isShowNumberOfItemsHint: boolean;
  shouldShuffle: boolean;
  variant: InquisitiveActivitiesResponseVariant;
}) {
  const dropItems = initialiseDropItems({
    items,
    categories,
    showAnswer,
    isShowNumberOfItemsHint,
  });

  return {
    dragItems: initialiseDragItems({ items, shouldShuffle, variant }),
    dropItems,
  };
}

export function resetIncorrectItems(
  currentDragItems: DragItem[],
  currentDropItems: DropItem[]
) {
  const updatedDragItems = [...currentDragItems];
  const updatedDropItems = [...currentDropItems];

  // Find incorrectly placed items in all drop zones
  currentDropItems.forEach((dropItem, dropIndex) => {
    const updatedZones = dropItem.zones.map(zone => {
      // If the item is incorrect, reset it
      if (zone.isCorrect === false && zone.dragItemId) {
        // Find the drag item that was incorrectly placed
        const dragItemIndex = updatedDragItems.findIndex(
          drag => drag.id === zone.dragItemId
        );

        if (dragItemIndex !== -1) {
          // Reset the drag item to not dropped
          updatedDragItems[dragItemIndex] = {
            ...updatedDragItems[dragItemIndex],
            isDropped: false,
          };
        }

        // Clear the drop zone
        return {
          dragItemId: null,
          previewItemId: null,
          isCorrect: undefined,
        };
      }

      // Clear isCorrect for all zones to reset the checked state
      return {
        ...zone,
        isCorrect: undefined,
      };
    });

    updatedDropItems[dropIndex] = {
      ...updatedDropItems[dropIndex],
      zones: updatedZones,
    };
  });

  return {
    dragItems: updatedDragItems,
    dropItems: updatedDropItems,
  };
}
